import { useCallback, useEffect, useState } from 'react';
import { getLastAnalysisRun } from '../../../../services/AnalysisApiClient';
import { getVoteTally, VoteTallyRow } from '../../../../services/ApprovalEnvelopeApiClient';

export function useVoteTally(analysisId: string | null, runId: string | null) {
  const [tally, setTally] = useState<VoteTallyRow | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [resolvedRunId, setResolvedRunId] = useState<string | null>(null);
  const [refreshKey, setRefreshKey] = useState(0);

  const retry = useCallback(() => setRefreshKey((k) => k + 1), []);

  useEffect(() => {
    let cancelled = false;
    if (!analysisId) {
      setTally(null);
      setError(null);
      setResolvedRunId(null);
      return () => {
        cancelled = true;
      };
    }

    setLoading(true);
    setError(null);

    void (async () => {
      try {
        let effectiveRunId = runId?.trim() || null;
        if (!effectiveRunId) {
          const last = await getLastAnalysisRun(analysisId);
          effectiveRunId = last?.runId ?? null;
        }
        if (cancelled) return;
        setResolvedRunId(effectiveRunId);
        if (!effectiveRunId) {
          setTally(null);
          return;
        }
        const next = await getVoteTally(analysisId, effectiveRunId);
        if (!cancelled) setTally(next);
      } catch (e) {
        if (!cancelled) {
          setTally(null);
          setError(e instanceof Error ? e.message : 'Failed to load vote tally.');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [analysisId, runId, refreshKey]);

  return { tally, loading, error, resolvedRunId, retry };
}
